import ChapterBadge from "./ChapterBadge";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  ShieldCheck,
  Users,
  MagnifyingGlass,
  Lock,
  Buildings,
  ClipboardText
} from "@phosphor-icons/react";

const SecuritySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const securityFeatures = [
    {
      icon: ShieldCheck,
      title: "Role-Based Access",
      description: "Granular permissions ensure the right people see the right information.",
    },
    {
      icon: MagnifyingGlass,
      title: "Source-Aware Answers",
      description: "Every insight is traceable to its original source for verification.",
    },
    {
      icon: Lock,
      title: "Controlled Permissions",
      description: "Enterprise-grade access controls and data governance policies.",
    },
    {
      icon: Buildings,
      title: "Enterprise Deployment",
      description: "Ready for your infrastructure with flexible deployment options.",
    },
    {
      icon: ClipboardText,
      title: "Auditable Outputs",
      description: "Complete audit trails for compliance and accountability.",
    },
    {
      icon: Users,
      title: "Responsible Handling",
      description: "Thoughtful handling of sensitive enterprise information.",
    },
  ];

  return (
    <section 
      id="security"
      ref={ref}
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 relative"
      data-testid="security-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#0A0A0B]" />

      <div className="relative z-10 max-w-5xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <ChapterBadge number="12" name="Security" />
          <span className="mono-label">Trust & Security</span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Outfit']">
            Enterprise-grade security
          </h2>
          <p className="mt-4 text-[#A1A1AA] max-w-xl mx-auto text-base md:text-lg">
            Built for the security and compliance requirements of modern enterprises.
          </p>
        </motion.div>

        {/* Security Features Grid */}
        <div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="security-features-grid"
        >
          {securityFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="p-5 rounded-xl bg-[#0F0F10]/50 border border-white/5 hover:border-white/10 transition-colors"
              data-testid={`security-feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center mb-3">
                <feature.icon size={20} weight="duotone" className="text-[#00E5FF]" />
              </div>
              <h3 className="text-base font-semibold text-white mb-1 font-['Outfit']">
                {feature.title}
              </h3>
              <p className="text-sm text-[#A1A1AA]">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Trust Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 text-center"
        >
          <p className="text-sm text-[#71717A] max-w-lg mx-auto">
            OnTenacity is designed with security at its core, ensuring your enterprise 
            data remains protected while delivering actionable intelligence.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default SecuritySection;
